// Push-notification lifecycle. Permission is requested only on explicit user action (never at
// boot). Foreground notifications surface as in-app toasts (no system alert); taps route via
// the registered deep-link handler.
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { toast } from "sonner";
import { pushDevicesApi } from "@/api/client";
import { stableDeviceId, isNative } from "@/native/capacitor";

let currentDeviceId = null;
let routeHandler = null;
export const setPushRouteHandler = (fn) => { routeHandler = fn; };

export async function requestAndRegister() {
  if (!isNative()) return { ok: false, reason: "not-native" };
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return { ok: false, reason: "denied" };
  await PushNotifications.register();
  return { ok: true };
}

// "prompt" | "prompt-with-rationale" | "granted" | "denied" on native; "web" in the browser.
export async function getPushPermission() {
  if (!isNative()) return "web";
  try { return (await PushNotifications.checkPermissions()).receive; } catch { return "unknown"; }
}

// Silent launch refresh: re-register only when already granted (never prompts) so tokens rotate.
export async function registerIfGranted() {
  if ((await getPushPermission()) !== "granted") return false;
  try { await PushNotifications.register(); return true; } catch { return false; }
}

export async function initPushListeners() {
  if (!isNative()) return;
  currentDeviceId = await stableDeviceId();
  // New / refreshed APNs token -> upsert on the backend.
  await PushNotifications.addListener("registration", async (token) => {
    try {
      await pushDevicesApi.register({
        device_id: currentDeviceId, apns_token: token.value, apns_env: "production",
        app_version: "1.0.0", os_version: Capacitor.getPlatform(), model_metadata: "ios",
      });
    } catch { /* retried on next registration */ }
  });
  // Foreground delivery: no system banner — show an in-app toast with a View action.
  await PushNotifications.addListener("pushNotificationReceived", (n) => {
    const path = n?.data?.deep_link_path;
    toast(n?.title || "Notification", {
      description: n?.body,
      action: (path && routeHandler) ? { label: "View", onClick: () => routeHandler(path) } : undefined,
    });
  });
  // Background tap -> route to the payload's deep link.
  await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const path = action?.notification?.data?.deep_link_path;
    if (path && routeHandler) routeHandler(path);
  });
}

export async function teardownPush({ revoke = true } = {}) {
  if (!isNative()) return;
  try { if (revoke && currentDeviceId) await pushDevicesApi.revoke(currentDeviceId); } catch { /* ignore */ }
  try { await PushNotifications.removeAllListeners(); } catch { /* ignore */ }
}
